import { TaModal } from '@components/shared/TaModal/TaModal';
import { useAppbarTitle } from '@hooks/useAppbarTitle';
import { usePageTitle } from '@hooks/usePageTitle';
import { useWindowSize } from '@hooks/useWindowSize';
import { mdiArrowLeft, mdiPaperclip } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Button, Skeleton } from '@mui/material';
import { hasAdminSecondaryRole } from 'services/auth/authUtils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetGuideline } from 'services/monitoring-offers';
import { useUsersStore } from 'services/users/usersStore';
import SubmitModal from '../components/SubmitModal';

const styles = {
	container: {
		display: 'flex',
		flexDirection: 'column',
		boxShadow: 3,
		position: 'relative',
		bgcolor: 'white',
		borderRadius: 3,
	},
};

export const MonitoringTopicPage = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const filesCount = searchParams.get('files_count');

	const [submitModalOpen, setSubmitModalOpen] = useState(false);
	const { user } = useUsersStore();

	const { t } = useTranslation();
	const size = useWindowSize();

	const { topicId, id: phaseId } = useParams();
	const { data: guidelines, isLoading, error } = useGetGuideline(topicId);
	const isFarmerPath = user?.role?.toLowerCase() === 'farmer';
	
	// Check if the user has admin secondary role
	const isAdmin = hasAdminSecondaryRole();
	
	// Determine if the user can upload files (either not a farmer or is a farmer with admin rights)
	const canUploadFiles = !isFarmerPath || isAdmin;
	
	// Determine the button text based on user role and permissions
	const buttonText = isFarmerPath && canUploadFiles 
		? t('submitData') 
		: t('uploadedFiles');

	const requestId = localStorage.getItem('request_id');
	const categoryId = localStorage.getItem('category_id');

	useEffect(() => {
		if (topicId) {
			localStorage.setItem('topic_id', topicId);
		}
		if (phaseId) {
			localStorage.setItem('phase_id', phaseId);
		}
	}, [topicId, categoryId, phaseId]);

	usePageTitle(t('monitoringProtocols'));
	useAppbarTitle('Monitoring Protocols');

	const toggleSubmitModal = () => {
		setSubmitModalOpen(!submitModalOpen);
	};

	return (
		<Box
			sx={{
				...styles.container,
				minHeight: size.width > 768 ? '100%' : 'auto',
			}}
		>
			<div className="p-10">
				{isLoading ? (
					<>
						{Array.from({ length: 3 }).map((_, index) => (
							<div key={index}>
								<Skeleton variant="text" width="100%" height={30} />
								<Skeleton variant="text" width="80%" height={30} />
								<Skeleton variant="text" width="50%" height={30} />
								<Skeleton variant="text" width="70%" height={30} />
								<Skeleton variant="text" width="90%" height={30} />
							</div>
						))}
						<Skeleton variant="rectangular" width="100%" height={200} />
					</>
				) : (
					<>
						<div className="flex justify-center items-center">
							<div className="absolute left-[20px] max-md:left-[10px] max-md:top-3">
								<Button
									className="w-[120px]"
									type="submit"
									onClick={() => navigate(-1)}
									variant="text"
									sx={{ color: 'black' }}
									startIcon={<Icon path={mdiArrowLeft} size={1} />}
								>
									<span className="max-xsm:hidden">{t('back')}</span>
								</Button>
							</div>

							<div className="flex items-center justify-center flex-col my-5">
								<h2 className="text-2xl font-bold mb-2">{guidelines?.topic_name}</h2>
								<h3 className="text-base text-dark">{t('topicGuideline')}</h3>
							</div>
							<div className="items-center justify-center absolute right-6 hidden md:flex">
								<Button
									type="submit"
									variant="text"
									onClick={toggleSubmitModal}
									sx={{ backgroundColor: '#FFAA2B', color: 'black' }}
									startIcon={<Icon path={mdiPaperclip} size={1} />}
								>
									{buttonText} ({filesCount || 0})
								</Button>
							</div>
						</div>
						{guidelines?.id ? (
							<div dangerouslySetInnerHTML={{ __html: guidelines?.content }}></div>
						) : (
							<div className="flex items-center justify-center flex-col my-5 gap-4">
								<span className="text-error uppercase font-bold">{t('somethingWentWrong')}</span>
								<span>{t('reloadPage')}</span>
								<Button
									type="submit"
									variant="text"
									onClick={() => window.location.reload()}
									sx={{ backgroundColor: '#038877', color: 'white' }}
								>
									{t('reload')}
								</Button>
							</div>
						)}
					</>
				)}
				<div className="w-full justify-center items-center mt-10 md:hidden flex">
					<Button
						type="submit"
						variant="text"
						onClick={toggleSubmitModal}
						sx={{ backgroundColor: '#FFAA2B', color: 'black', width: '100%' }}
						startIcon={<Icon path={mdiPaperclip} size={1} />}
					>
						{buttonText} ({filesCount || 0})
					</Button>
				</div>
			</div>
			<TaModal
				open={submitModalOpen}
				onClose={toggleSubmitModal}
				title={buttonText}
				showActions={false}
			>
				<div className="flex flex-col gap-[10px] mt-[20px]">
					<SubmitModal onClose={toggleSubmitModal} />
				</div>
			</TaModal>
		</Box>
	);
};
